import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Link,
  Container,
  Menu,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar,
  Autocomplete,
  Tabs,
  Tab,
  Chip,
  ButtonGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n';
import GroupedBalances from '../components/GroupedBalances';
import TransactionsList from '../components/TransactionsList';
import SyncProgress from '../components/SyncProgress';
import TokenIcon from '../components/TokenIcon';
import AddTransactionModal from '../AddTransactionModal';
import WalletRow from '../components/WalletRow';
import { useWalletsPage } from '../hooks/useWalletsPage';
import api from '../services/api';
import ApexChart from 'react-apexcharts';

import { useCoinMarketValueHistory } from '../hooks/useCoinMarketValueHistory';
import { useCoinPriceBalanceData } from '../hooks/useCoinPriceBalanceData';
import { useAllCoinsPriceBalanceData } from '../hooks/useAllCoinsPriceBalanceData';
import { useAllCoinsPriceHistory } from '../hooks/useAllCoinsPriceHistory';
import { useAllCoinsPriceBalanceDataCombined } from '../hooks/useAllCoinsPriceBalanceDataCombined';
import { getCachedChart } from '../services/chartCacheService';
import { getSafePortfolioValue } from '../utils/balanceValidation';
import { TRANSACTIONS_ENABLED } from '../config/featureFlags';
import PortfolioSnapshotChart from '../components/PortfolioSnapshotChart';



function PortfolioPage() {
  const { t } = useTranslation();

  // Move PORTFOLIO_RANGES here so 't' is defined
  const PORTFOLIO_RANGES = [
    { label: t('portfolio.range.24h'), value: '24h' },
    { label: t('portfolio.range.1d'), value: '1d' },
    { label: t('portfolio.range.5d'), value: '5d' },
    { label: t('portfolio.range.1m'), value: '1m' },
    { label: t('portfolio.range.6m'), value: '6m' },
    { label: t('portfolio.range.1y'), value: '1y' },
    { label: t('portfolio.range.5y'), value: '5y' },
    { label: t('portfolio.range.all'), value: 'all' },
  ];

  const [chartMode, setChartMode] = useState('marketValue'); // 'marketValue' or 'balanceOnly'
  const {
    // State
    activeTab,
    wallets,
    open,
    modalType,
    newWallet,
    newTransaction,
    editingTransaction,
    manualHoldings,
    manualHoldingsLoading,
    loading,
    error,
    modalError,
    currentUser,
    walletTransactions,
    loadingTransactions,
    deleteConfirmOpen,
    walletToDelete,
    transactionFilters,
    transactionSort,
    sortColumn,
    sortDirection,
    visibleColumns,
    anchorEl,
    columnsDialogOpen,
    tempVisibleColumns,
    isRTL: isRTLState,
    snackbar,
    realOnChainBalance,
    missingAmount,
    uniqueCoins,
    blockchains,
    blockchainsLoading,
    coins,
    coinsLoading,
    refreshCoins,
    syncLoading,
    syncError,
    groupedBalances,
    transactions,
    syncProgress,
    transactionPagination,
    prices,
    loadingPrices,
    pricesError,
    refreshPrices,
    refreshWithIcons,
    forceRefreshPrices,
    lastUpdate,
    freshnessInfo,
    isRefreshing,
    allCoinIdentifiers,
    allTransactionCoins,
    allColumns,
    sortedWallets,
    blockchainSymbolMap,
    backendBlockchainMap,
    columnRenderers,
    tempHiddenColumns,
    manualHoldingsSearchTerm,
    manualHoldingsSortColumn,
    manualHoldingsSortDirection,
    manualHoldingsDeleteConfirmOpen,
    manualHoldingsToDelete,
    manualHoldingsAllColumns,
    manualHoldingsVisibleColumns,
    manualHoldingsColumnsDialogOpen,
    manualHoldingsTempVisibleColumns,
    manualHoldingsTempHiddenColumns,
    filteredManualHoldings,
    sortedManualHoldings,
    manualHoldingsColumnRenderers,
    portfolioGains,
    loadingGains,

    // Actions
    setActiveTab,
    setWallets,
    setOpen,
    setModalType,
    setNewWallet,
    setNewTransaction,
    setEditingTransaction,
    setManualHoldings,
    setManualHoldingsLoading,
    setManualHoldingsSearchTerm,
    setManualHoldingsSortColumn,
    setManualHoldingsSortDirection,
    setManualHoldingsDeleteConfirmOpen,
    setManualHoldingsToDelete,
    setManualHoldingsVisibleColumns,
    setManualHoldingsColumnsDialogOpen,
    setManualHoldingsTempVisibleColumns,
    setManualHoldingsTempHiddenColumns,
    setLoading,
    setError,
    setModalError,
    setWalletTransactions,
    setLoadingTransactions,
    setDeleteConfirmOpen,
    setWalletToDelete,
    setTransactionFilters,
    setTransactionSort,
    setSortColumn,
    setSortDirection,
    setVisibleColumns,
    setAnchorEl,
    setColumnsDialogOpen,
    setTempVisibleColumns,
    setIsRTL,
    setSnackbar,
    setRealOnChainBalance,
    setMissingAmount,
    setUniqueCoins,
    setPortfolioGains,
    setLoadingGains,

    // Handlers
    handleTabChange,
    handleTransactionsPageChange,
    handleFilterChange,
    handleTransactionSort,
    handleSyncWallet,
    handleDeleteWallet,
    handleManualRefresh,
    handleSubmit,
    fetchWalletTransactions,
    handleRefreshWallet,
    resetWalletForm,
    resetTransactionForm,
    handleClickOpen,
    handleOpenModal,
    handleClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSort,
    handleColumnMenuOpen,
    handleColumnMenuClose,
    handleToggleColumn,
    handleMoveColumnUp,
    handleMoveColumnDown,
    handleHideColumn,
    handleShowColumn,
    handleOpenColumnsDialog,
    handleCloseColumnsDialog,
    handleSaveColumnsDialog,
    fetchManualHoldings,
    fetchPortfolioGains,
    calculateBasicGains,
    handleAddManualHolding,
    handleEditManualHolding,
    handleDeleteManualHolding,
    handleManualHoldingsSort,
    handleManualHoldingsSearch,
    handleManualHoldingsDeleteClick,
    handleManualHoldingsDeleteCancel,
    handleManualHoldingsDeleteConfirm,
    handleManualHoldingsOpenColumnsDialog,
    handleManualHoldingsCloseColumnsDialog,
    handleManualHoldingsSaveColumnsDialog,
    handleManualHoldingsMoveColumnUp,
    handleManualHoldingsMoveColumnDown,
    handleManualHoldingsHideColumn,
    handleManualHoldingsShowColumn,
    getLastUpdateText,
    getFreshnessDisplay,
    formatCurrency,
    loadInitialData,
    syncWallet,
    addWallet,
    refreshGroupedBalances,
    fetchTransactions,
    getWallets,
    deleteWallet,
    clearError,
    clearSyncProgress,
    clearGroupedBalances,
    setTransactions,
    resetManualHoldingsColumns
  } = useWalletsPage();

  const [chartRange, setChartRange] = React.useState('6m');
  const [selectedCoin, setSelectedCoin] = React.useState('all'); // 'all' or specific coin
  const [selectedGraph, setSelectedGraph] = React.useState('marketValue'); // 'price', 'balance', 'marketValue', 'cost', 'marketValueCost', 'gains'
  const { data: coinMarketValueHistory, loading: coinMarketValueLoading, error: coinMarketValueError } = useCoinMarketValueHistory(chartRange);

  // Add new hook for coin price and balance data
  const { data: coinPriceBalanceData, loading: coinPriceBalanceLoading, error: coinPriceBalanceError } = useCoinPriceBalanceData(
    selectedCoin !== 'all' ? selectedCoin : null,
    chartRange
  );

  const { data: allCoinsPriceBalanceData, loading: allCoinsPriceBalanceLoading, error: allCoinsPriceBalanceError } = useAllCoinsPriceBalanceData(
    selectedCoin === 'all' ? chartRange : null
  );

  const { data: allCoinsPriceHistory, loading: allCoinsPriceHistoryLoading, error: allCoinsPriceHistoryError } = useAllCoinsPriceHistory(
    selectedCoin === 'all' ? chartRange : null
  );

  const { data: allCoinsPriceBalanceDataCombined, loading: allCoinsPriceBalanceDataCombinedLoading, error: allCoinsPriceBalanceDataCombinedError } = useAllCoinsPriceBalanceDataCombined(
    selectedCoin === 'all' ? chartRange : null
  );



  // Add state for coin price history
  const [coinPriceHistory, setCoinPriceHistory] = React.useState(null);
  const [coinPriceHistoryLoading, setCoinPriceHistoryLoading] = React.useState(false);
  const [coinPriceHistoryError, setCoinPriceHistoryError] = React.useState(null);

  // Fetch coin price history when a single coin is selected
  React.useEffect(() => {
    let cancelled = false;
    async function fetchPriceHistory() {
      if (selectedCoin === 'all' || !selectedCoin) {
        setCoinPriceHistory(null);
        return;
      }
      setCoinPriceHistoryLoading(true);
      setCoinPriceHistoryError(null);
      try {
        const data = await getCachedChart(selectedCoin, chartRange);
        if (!cancelled) setCoinPriceHistory(data);
      } catch (err) {
        if (!cancelled) setCoinPriceHistoryError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setCoinPriceHistoryLoading(false);
      }
    }
    fetchPriceHistory();
    return () => { cancelled = true; };
  }, [selectedCoin, chartRange]);

  const [showNumbers, setShowNumbers] = React.useState(true);



  // Calculate processedBalances (same as GroupedBalances.js)
  const processedBalances = React.useMemo(() => {
    if (!groupedBalances) return [];
    
    // Merge prices with balances
    const balancesWithPrices = groupedBalances.map(balance => {
      // Get price from the prices object (case-insensitive lookup)
      const upperCoinSymbol = balance.coin?.toUpperCase();
      const priceData = prices[upperCoinSymbol] || prices[balance.coin];
      const price = typeof priceData === 'object' && priceData.usd ? priceData.usd : (typeof priceData === 'number' ? priceData : 0);
      
      return {
        ...balance,
        price: priceData || 0 // Assign the price data to the balance
      };
    });
    
    const totalPortfolioValue = balancesWithPrices.reduce((sum, balance) => {
      const price = typeof balance.price === 'number' ? balance.price : (balance.price?.usd || 0);
      return sum + (balance.total_balance * price);
    }, 0);
    
    const arr = balancesWithPrices.map(balance => {
      const coinGains = portfolioGains[balance.coin];
      const price = typeof balance.price === 'number' ? balance.price : (balance.price?.usd || 0);
      const marketValue = balance.total_balance * price;
      const percentage = totalPortfolioValue > 0 ? (marketValue / totalPortfolioValue) * 100 : 0;
      return {
        ...balance,
        marketValue,
        percentage,
        gain24h: coinGains?.gain_24h || 0,
        gain24hPercent: typeof coinGains?.gain_24h_percent === 'number' ? coinGains.gain_24h_percent : 0,
        gain1m: coinGains?.gain_1m || 0,
        gain1mPercent: typeof coinGains?.gain_1m_percent === 'number' ? coinGains.gain_1m_percent : 0,
        gainAllTime: coinGains?.gain_all_time || 0,
        gainAllTimePercent: typeof coinGains?.gain_all_time_percent === 'number' ? coinGains.gain_all_time_percent : 0,
      };
    });
    return arr;
  }, [groupedBalances, portfolioGains, prices]);

  // Calculate total portfolio value from processedBalances (excluding suspicious balances)
  const totalPortfolioValue = React.useMemo(() => {
    return getSafePortfolioValue(processedBalances);
  }, [processedBalances]);

  // Calculate global gains and percentages from processedBalances
  const globalGains = React.useMemo(() => {
    if (!processedBalances.length) return { gain24h: 0, gain24hPercent: 0, gain1m: 0, gain1mPercent: 0, gainAllTime: 0, gainAllTimePercent: 0 };
    const totalMarketValue = processedBalances.reduce((sum, b) => sum + (b.marketValue || 0), 0);
    const gain24h = processedBalances.reduce((sum, b) => sum + (b.gain24h || 0), 0);
    const gain1m = processedBalances.reduce((sum, b) => sum + (b.gain1m || 0), 0);
    const gainAllTime = processedBalances.reduce((sum, b) => sum + (b.gainAllTime || 0), 0);
    const gain24hPercent = totalMarketValue ? processedBalances.reduce((sum, b) => sum + (b.marketValue * b.gain24hPercent), 0) / totalMarketValue : 0;
    const gain1mPercent = totalMarketValue ? processedBalances.reduce((sum, b) => sum + (b.marketValue * b.gain1mPercent), 0) / totalMarketValue : 0;
    const gainAllTimePercent = totalMarketValue ? processedBalances.reduce((sum, b) => sum + (b.marketValue * b.gainAllTimePercent), 0) / totalMarketValue : 0;
    return {
      gain24h,
      gain24hPercent,
      gain1m,
      gain1mPercent,
      gainAllTime,
      gainAllTimePercent
    };
  }, [processedBalances]);

  useEffect(() => {
    fetchPortfolioGains().catch(error => {
      console.error('Portfolio gains failed to load:', error);
    });
  }, []);

  return (
    <Container maxWidth={false} disableGutters sx={{ mt: 2, mb: 2, px: { xs: 1.5, sm: 1.5 } }}>
      {/* Portfolio Value & Gains Section */}
      <Box sx={{ position: 'relative', mb: 2, maxWidth: '100%', mx: 0, pt: 1 }}>
        {/* Eye icon for hide/show balances */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            zIndex: 1,
            right: isRTL() ? 'auto' : 0,
            left: isRTL() ? 0 : 'auto',
          }}
        >
          <IconButton size="small" onClick={() => setShowNumbers(v => !v)}>
            {showNumbers ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          {/* Portfolio Value */}
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.1, fontSize: { xs: '2.1rem', sm: '2.5rem' } }}>
            {showNumbers ? ('$' + Math.ceil(totalPortfolioValue).toLocaleString()) : '****'}
          </Typography>
          {/* 24H Gain */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color:
                (globalGains.gain24h || 0) > 0
                  ? 'success.main'
                  : (globalGains.gain24h || 0) < 0
                  ? 'error.main'
                  : 'text.secondary',
              fontSize: '1.05rem',
              lineHeight: 1.2,
            }}
          >
            {showNumbers
              ? `${globalGains.gain24h >= 0 ? '+' : ''}$${Math.round(globalGains.gain24h).toLocaleString()} (${globalGains.gain24hPercent >= 0 ? '+' : ''}${isFinite(globalGains.gain24hPercent) ? globalGains.gain24hPercent.toFixed(1) : '0.0'}%)`
              : `**** (${globalGains.gain24hPercent >= 0 ? '+' : ''}${isFinite(globalGains.gain24hPercent) ? globalGains.gain24hPercent.toFixed(1) : '0.0'}%)`} {' '}
            <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>{t('portfolio.24h')}</span>
          </Typography>
          {/* 1M Gain */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color:
                (globalGains.gain1m || 0) > 0
                  ? 'success.main'
                  : (globalGains.gain1m || 0) < 0
                  ? 'error.main'
                  : 'text.secondary',
              fontSize: '1.05rem',
              lineHeight: 1.2,
            }}
          >
            {showNumbers
              ? `${globalGains.gain1m >= 0 ? '+' : ''}$${Math.round(globalGains.gain1m).toLocaleString()} (${globalGains.gain1mPercent >= 0 ? '+' : ''}${isFinite(globalGains.gain1mPercent) ? globalGains.gain1mPercent.toFixed(1) : '0.0'}%)`
              : `**** (${globalGains.gain1mPercent >= 0 ? '+' : ''}${isFinite(globalGains.gain1mPercent) ? globalGains.gain1mPercent.toFixed(1) : '0.0'}%)`} {' '}
            <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>{t('portfolio.1m')}</span>
          </Typography>
          {/* All-Time Gain */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color:
                (globalGains.gainAllTime || 0) > 0
                  ? 'success.main'
                  : (globalGains.gainAllTime || 0) < 0
                  ? 'error.main'
                  : 'text.secondary',
              fontSize: '1.05rem',
              lineHeight: 1.2,
            }}
          >
            {showNumbers
              ? `${globalGains.gainAllTime >= 0 ? '+' : ''}$${Math.round(globalGains.gainAllTime).toLocaleString()} (${globalGains.gainAllTimePercent >= 0 ? '+' : ''}${isFinite(globalGains.gainAllTimePercent) ? globalGains.gainAllTimePercent.toFixed(1) : '0.0'}%)`
              : `**** (${globalGains.gainAllTimePercent >= 0 ? '+' : ''}${isFinite(globalGains.gainAllTimePercent) ? globalGains.gainAllTimePercent.toFixed(1) : '0.0'}%)`} {' '}
            <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>{t('portfolio.allTime')}</span>
          </Typography>
          {/* Last updated - Only show when there are balances */}
          {groupedBalances && groupedBalances.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {getLastUpdateText()}
            </Typography>
          )}
        </Box>
      </Box>

            {/* Balance-only mode: replace the transaction-derived history
                charts with a simple daily snapshot trend line. See
                docs/SIMPLIFICATION_PLAN.md. */}
      {!TRANSACTIONS_ENABLED && groupedBalances && groupedBalances.length > 0 && (
        <PortfolioSnapshotChart />
      )}

            {/* Coin Market Value Chart - Only show when there are balances */}
      {TRANSACTIONS_ENABLED && groupedBalances && groupedBalances.length > 0 && (
        <Box sx={{ mb: 2, width: '100%', maxWidth: '100%', mx: 0, bgcolor: '#fff', borderRadius: 2, p: { xs: 0.5, sm: 2 }, boxShadow: 0, minHeight: 180 }}>
        {/* Coin Selector and Chart Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, gap: 2, alignItems: 'center', p: { xs: 0, sm: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120, height: 32 }}>
              <Select
                value={selectedCoin}
                onChange={(e) => {
                  setSelectedCoin(e.target.value);
                }}
                displayEmpty
                sx={{ fontSize: '0.8rem', height: 32, minHeight: 32, py: 0, '.MuiSelect-select': { py: 0.5 } }}
              >
                <MenuItem value="all">{t('portfolio.allCoins')}</MenuItem>
                {coinMarketValueHistory?.coins && Object.keys(coinMarketValueHistory.coins).sort().map((coin) => (
                  <MenuItem key={coin} value={coin}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TokenIcon coinSymbol={coin} size={16} />
                      {coin}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Graph Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150, height: 32 }}>
              <Select
                value={selectedGraph}
                onChange={(e) => {
                  setSelectedGraph(e.target.value);
                }}
                displayEmpty
                sx={{ fontSize: '0.8rem', height: 32, minHeight: 32, py: 0, '.MuiSelect-select': { py: 0.5 } }}
              >
                {selectedCoin === 'all' && (
                  <MenuItem value="price">{t('portfolio.averagePrice')}</MenuItem>
                )}
                {selectedCoin === 'all' && (
                  <MenuItem value="balance">{t('portfolio.totalBalance')}</MenuItem>
                )}
                {selectedCoin === 'all' && (
                  <MenuItem value="marketValue">{t('portfolio.totalMarketValue')}</MenuItem>
                )}
                {selectedCoin === 'all' && (
                  <MenuItem value="cost">{t('portfolio.totalCost')}</MenuItem>
                )}
                {selectedCoin === 'all' && (
                  <MenuItem value="marketValueCost">{t('portfolio.marketValueAndCost')}</MenuItem>
                )}
                {selectedCoin === 'all' && (
                  <MenuItem value="gains">{t('portfolio.totalGainsLosses')}</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="price">{selectedCoin} Price</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="balance">{selectedCoin} Balance</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="marketValue">{selectedCoin} Market Value</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="cost">{selectedCoin} Cost</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="marketValueCost">{selectedCoin} Market Value & Cost</MenuItem>
                )}
                {selectedCoin !== 'all' && (
                  <MenuItem value="gains">{selectedCoin} Gains/Losses</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </Box>


        
        {/* Coin Market Value Chart */}
        {coinMarketValueLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
            <CircularProgress />
          </Box>
        ) : coinMarketValueError ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
            <Typography color="error" variant="body2">{t('errors.failedToLoadCoinMarketValue')}</Typography>
          </Box>
        ) : selectedCoin === 'all' ? (
          // All coins combined portfolio charts (5 charts like individual coin)
          allCoinsPriceBalanceDataCombinedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
              <CircularProgress />
            </Box>
          ) : allCoinsPriceBalanceDataCombinedError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
              <Typography color="error" variant="body2">Failed to load all coins portfolio data</Typography>
            </Box>
          ) : allCoinsPriceBalanceDataCombined && allCoinsPriceBalanceDataCombined.data && allCoinsPriceBalanceDataCombined.data.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Portfolio Average Price Chart */}
              {selectedGraph === 'price' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#4caf50', fontWeight: 600 }}>
                  {t('portfolio.averagePrice')}
                </Typography>
                <ApexChart
                  type="area"
                  height={120}
                  series={[
                    {
                      name: 'Average Price',
                      data: (() => {
                        const chartData = allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.price]);
                        console.log('[PortfolioPage] Chart data for Average Price (first 5):', chartData.slice(0, 5).map(p => [new Date(p[0]).toISOString().split('T')[0], p[1]]));
                        console.log('[PortfolioPage] Chart data for Average Price (last 5):', chartData.slice(-5).map(p => [new Date(p[0]).toISOString().split('T')[0], p[1]]));
                        console.log('[PortfolioPage] Chart data for Average Price (all):', chartData.map(p => [new Date(p[0]).toISOString().split('T')[0], p[1]]));
                        return chartData;
                      })()
                    }
                  ]}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      zoom: { enabled: false },
                      background: '#fff',
                      animations: { enabled: true },
                      sparkline: false,
                      fontFamily: 'inherit',
                    },
                    dataLabels: { enabled: false },
                    xaxis: {
                      type: 'datetime',
                      labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                      tooltip: { enabled: false },
                      crosshairs: { show: false },
                    },
                                         yaxis: {
                       show: true,
                       labels: {
                         show: true,
                         style: { fontSize: '10px', color: '#4caf50' },
                         formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '0.0000'))
                       },
                       axisBorder: { show: false },
                       axisTicks: { show: false },
                       
                      // Force chart to recalculate range when data changes
                      forceNiceScale: true,
                      floating: false
                    },
                                         stroke: { 
                       width: 3, 
                       curve: 'straight',
                       colors: ['#4caf50']
                     },
                     // Handle missing data points better
                     connectNulls: false,
                     colors: ['#4caf50'],
                     fill: {
                       type: 'gradient',
                       gradient: {
                         shade: 'light',
                         type: 'vertical',
                         shadeIntensity: 0.3,
                         gradientToColors: ['#4caf50'],
                         inverseColors: false,
                         opacityFrom: 0.6,
                         opacityTo: 0.1,
                         stops: [0, 100]
                       }
                     },
                     markers: { size: 0 },
                    tooltip: {
                      enabled: true,
                      x: { format: 'dd MMM yyyy HH:mm' },
                      y: {
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '0.0000'))
                      },
                      theme: 'light',
                      style: { fontSize: '12px' },
                      marker: { show: false },
                    },
                    grid: {
                      show: true,
                      borderColor: '#e0e0e0',
                      strokeDashArray: 4,
                      xaxis: { lines: { show: true } },
                      yaxis: { lines: { show: true } },
                    },
                    legend: { show: false },
                  }}
                />
              </Box>
              )}

              {/* Portfolio Total Balance Chart */}
              {selectedGraph === 'balance' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#ff9800', fontWeight: 600 }}>
                  {t('portfolio.totalBalance')}
                </Typography>
                <ApexChart
                  type="area"
                  height={120}
                  series={[
                    {
                      name: 'Total Balance',
                      data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.balance])
                    }
                  ]}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      zoom: { enabled: false },
                      background: '#fff',
                      animations: { enabled: true },
                      sparkline: false,
                      fontFamily: 'inherit',
                    },
                    dataLabels: { enabled: false },
                    xaxis: {
                      type: 'datetime',
                      labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                      tooltip: { enabled: false },
                      crosshairs: { show: false },
                    },
                    yaxis: {
                      show: true,
                      labels: {
                        show: true,
                        style: { fontSize: '10px', color: '#ff9800' },
                        formatter: val => (val ? val.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '0.00000000')
                      },
                      axisBorder: { show: false },
                      axisTicks: { show: false },

                      // Ensure small values are visible by setting minimum scale
                      min: 0,
                      forceNiceScale: true,
                      floating: false
                    },
                    stroke: { 
                      width: 3, 
                      curve: 'straight',
                      colors: ['#ff9800']
                    },
                    colors: ['#ff9800'],
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.3,
                        gradientToColors: ['#ff9800'],
                        inverseColors: false,
                        opacityFrom: 0.6,
                        opacityTo: 0.1,
                        stops: [0, 100]
                      }
                    },
                    markers: { size: 0 },
                    tooltip: {
                      enabled: true,
                      x: { format: 'dd MMM yyyy HH:mm' },
                      y: {
                        formatter: val => (val ? val.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '0.00000000')
                      },
                      theme: 'light',
                      style: { fontSize: '12px' },
                      marker: { show: false },
                    },
                    grid: {
                      show: true,
                      borderColor: '#e0e0e0',
                      strokeDashArray: 4,
                      xaxis: { lines: { show: true } },
                      yaxis: { lines: { show: true } },
                    },
                    legend: { show: false },
                  }}
                />
              </Box>
              )}

              {/* Portfolio Total Market Value Chart */}
              {selectedGraph === 'marketValue' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#4caf50', fontWeight: 600 }}>
                  {t('portfolio.totalMarketValue')}
                </Typography>
                <ApexChart
                  type="area"
                  height={120}
                   series={[
                     {
                       name: 'Total Market Value',
                       data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.marketValue])
                     }
                   ]}
                   options={{
                     chart: {
                       toolbar: { show: false },
                       zoom: { enabled: false },
                       background: '#fff',
                       animations: { enabled: true },
                       sparkline: false,
                       fontFamily: 'inherit',
                     },
                     dataLabels: { enabled: false },
                     xaxis: {
                       type: 'datetime',
                       labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                       axisBorder: { show: false },
                       axisTicks: { show: false },
                       tooltip: { enabled: false },
                       crosshairs: { show: false },
                     },
                     yaxis: {
                       show: true,
                       labels: {
                         show: true,
                         style: { fontSize: '10px', color: '#4caf50' },
                         formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                       },
                       axisBorder: { show: false },
                       axisTicks: { show: false },

                     },
                                         stroke: { 
                      width: 3, 
                      curve: 'straight',
                      colors: ['#4caf50']
                    },
                    colors: ['#4caf50'],
                    fill: {
                      opacity: [0.6, 0]
                    },
                    markers: { size: 0 },
                     tooltip: {
                       enabled: true,
                       x: { format: 'dd MMM yyyy HH:mm' },
                       y: {
                         formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                       },
                       theme: 'light',
                       style: { fontSize: '12px' },
                       marker: { show: false },
                     },
                     grid: {
                       show: true,
                       borderColor: '#e0e0e0',
                       strokeDashArray: 4,
                       xaxis: { lines: { show: true } },
                       yaxis: { lines: { show: true } },
                     },
                     legend: { show: false },
                   }}
                 />
               </Box>
              )}

              {/* Portfolio Total Cost Chart */}
              {selectedGraph === 'cost' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#e91e63', fontWeight: 600 }}>
                  {t('portfolio.totalCost')}
                </Typography>
                <ApexChart
                  type="area"
                  height={120}
                  series={[
                    {
                      name: 'Total Cost',
                      data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.cost])
                    }
                  ]}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      zoom: { enabled: false },
                      background: '#fff',
                      animations: { enabled: true },
                      sparkline: false,
                      fontFamily: 'inherit',
                    },
                    dataLabels: { enabled: false },
                    xaxis: {
                      type: 'datetime',
                      labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                      tooltip: { enabled: false },
                      crosshairs: { show: false },
                    },
                    yaxis: {
                      show: true,
                      labels: {
                        show: true,
                        style: { fontSize: '10px', color: '#e91e63' },
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      axisBorder: { show: false },
                      axisTicks: { show: false },

                    },
                    stroke: { 
                      width: 3, 
                      curve: 'straight',
                      colors: ['#e91e63']
                    },
                    colors: ['#e91e63'],
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.3,
                        gradientToColors: ['#e91e63'],
                        inverseColors: false,
                        opacityFrom: 0.6,
                        opacityTo: 0.1,
                        stops: [0, 100]
                      }
                    },
                    markers: { size: 0 },
                    tooltip: {
                      enabled: true,
                      x: { format: 'dd MMM yyyy HH:mm' },
                      y: {
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      theme: 'light',
                      style: { fontSize: '12px' },
                      marker: { show: false },
                    },
                    grid: {
                      show: true,
                      borderColor: '#e0e0e0',
                      strokeDashArray: 4,
                      xaxis: { lines: { show: true } },
                      yaxis: { lines: { show: true } },
                    },
                    legend: { show: false },
                  }}
                />
              </Box>
              )}

              {/* Portfolio Combined Market Value & Cost Chart */}
              {selectedGraph === 'marketValueCost' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#9c27b0', fontWeight: 600 }}>
                  {t('portfolio.marketValueAndCost')}
                </Typography>
                <ApexChart
                  type="line"
                  height={120}
                  series={[
                    {
                      name: 'Total Market Value',
                      type: 'line',
                      data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.marketValue])
                    },
                    {
                      name: 'Total Cost',
                      type: 'line',
                      data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.cost])
                    }
                  ]}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      zoom: { enabled: false },
                      background: '#fff',
                      animations: { enabled: true },
                      sparkline: false,
                      fontFamily: 'inherit',
                    },
                    dataLabels: { enabled: false },
                    xaxis: {
                      type: 'datetime',
                      labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                      tooltip: { enabled: false },
                      crosshairs: { show: false },
                    },
                    yaxis: {
                      // Single Y-axis for both Market Value and Cost to ensure correct visual relationship
                      show: true,
                      labels: {
                        show: true,
                        style: { fontSize: '10px', color: '#666' },
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      axisBorder: { show: false },
                      axisTicks: { show: false },

                      min: (() => {
                        const marketValues = allCoinsPriceBalanceDataCombined.data.map(point => point.marketValue);
                        const costValues = allCoinsPriceBalanceDataCombined.data.map(point => point.cost);
                        const allValues = [...marketValues, ...costValues];
                        return Math.min(...allValues) * 0.95;
                      })(),
                      max: (() => {
                        const marketValues = allCoinsPriceBalanceDataCombined.data.map(point => point.marketValue);
                        const costValues = allCoinsPriceBalanceDataCombined.data.map(point => point.cost);
                        const allValues = [...marketValues, ...costValues];
                        return Math.max(...allValues) * 1.05;
                      })(),
                      forceNiceScale: false,
                      floating: false
                    },
                    stroke: { 
                      width: [2, 2], 
                      curve: 'straight'
                    },
                    colors: ['#4caf50', '#e91e63'],
                    markers: { size: 0 },
                    tooltip: {
                      enabled: true,
                      x: { format: 'dd MMM yyyy HH:mm' },
                      y: {
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      theme: 'light',
                      style: { fontSize: '12px' },
                      marker: { show: false },
                    },
                    grid: {
                      show: true,
                      borderColor: '#e0e0e0',
                      strokeDashArray: 4,
                      xaxis: { lines: { show: true } },
                      yaxis: { lines: { show: true } },
                    },
                    legend: {
                      show: true,
                      position: 'top',
                      horizontalAlign: 'left',
                      fontSize: '10px',
                      markers: { width: 8, height: 8 },
                      itemMargin: { horizontal: 10 }
                    },
                  }}
                />
              </Box>
              )}

              {/* Portfolio Total Gains/Losses Chart */}
              {selectedGraph === 'gains' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#ff5722', fontWeight: 600 }}>
                  {t('portfolio.totalGainsLosses')}
                </Typography>
                <ApexChart
                  type="area"
                  height={120}
                  series={[
                    {
                      name: 'Total Gains/Losses',
                      data: allCoinsPriceBalanceDataCombined.data.map(point => [point.timestamp, point.marketValue - point.cost])
                    }
                  ]}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      zoom: { enabled: false },
                      background: '#fff',
                      animations: { enabled: true },
                      sparkline: false,
                      fontFamily: 'inherit',
                    },
                    dataLabels: { enabled: false },
                    xaxis: {
                      type: 'datetime',
                      labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                      tooltip: { enabled: false },
                      crosshairs: { show: false },
                    },
                    yaxis: {
                      show: true,
                      labels: {
                        show: true,
                        style: { fontSize: '10px', color: '#ff5722' },
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      axisBorder: { show: false },
                      axisTicks: { show: false },

                    },
                    stroke: { 
                      width: 3, 
                      curve: 'straight',
                      colors: ['#ff5722']
                    },
                    colors: ['#ff5722'],
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.3,
                        gradientToColors: ['#ff5722'],
                        inverseColors: false,
                        opacityFrom: 0.6,
                        opacityTo: 0.1,
                        stops: [0, 100]
                      }
                    },
                    markers: { size: 0 },
                    tooltip: {
                      enabled: true,
                      x: { format: 'dd MMM yyyy HH:mm' },
                      y: {
                        formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                      },
                      theme: 'light',
                      style: { fontSize: '12px' },
                      marker: { show: false },
                    },
                    grid: {
                      show: true,
                      borderColor: '#e0e0e0',
                      strokeDashArray: 4,
                      xaxis: { lines: { show: true } },
                      yaxis: { lines: { show: true } },
                    },
                    legend: { show: false },
                  }}
                />
              </Box>
              )}

              {/* All Coins Price History Chart */}
            </Box>
          ) : null
        ) : coinMarketValueHistory && coinMarketValueHistory.coins && Object.keys(coinMarketValueHistory.coins).length > 0 ? (
          // Two separate charts for individual coin
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Price Chart */}
              {selectedGraph === 'price' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#1976d2', fontWeight: 600 }}>
                    {selectedCoin} Price
                  </Typography>
                  {coinPriceBalanceData && coinPriceBalanceData.data ? (
                    <ApexChart
                      type="area"
                      height={120}
                      series={[
                        {
                          name: 'Price',
                          data: coinPriceBalanceData.data.map(point => [point.timestamp, point.price])
                        }
                      ]}
                      options={{
                        chart: {
                          toolbar: { show: false },
                          zoom: { enabled: false },
                          background: '#fff',
                          animations: { enabled: true },
                          sparkline: false,
                          fontFamily: 'inherit',
                        },
                        dataLabels: { enabled: false },
                        xaxis: {
                          type: 'datetime',
                          labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                          axisBorder: { show: false },
                          axisTicks: { show: false },
                          tooltip: { enabled: false },
                          crosshairs: { show: false },
                        },
                        yaxis: {
                          show: true,
                          labels: {
                            show: true,
                            style: { fontSize: '10px', color: '#1976d2' },
                            formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '0.0000'))
                          },
                          axisBorder: { show: false },
                          axisTicks: { show: false },

                        },
                        stroke: { 
                          width: 3, 
                          curve: 'straight',
                          colors: ['#1976d2']
                        },
                        colors: ['#1976d2'],
                        fill: {
                          type: 'gradient',
                          gradient: {
                            shade: 'light',
                            type: 'vertical',
                            shadeIntensity: 0.3,
                            gradientToColors: ['#1976d2'],
                            inverseColors: false,
                            opacityFrom: 0.6,
                            opacityTo: 0.1,
                            stops: [0, 100]
                          }
                        },
                        markers: { size: 0 },
                        tooltip: {
                          enabled: true,
                          x: { format: 'dd MMM yyyy HH:mm' },
                          y: {
                            formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '0.0000'))
                          },
                          theme: 'light',
                          style: { fontSize: '12px' },
                          marker: { show: false },
                        },
                        grid: {
                          show: true,
                          borderColor: '#e0e0e0',
                          strokeDashArray: 4,
                          xaxis: { lines: { show: true } },
                          yaxis: { lines: { show: true } },
                        },
                        legend: { show: false },
                      }}
                    />
                  ) : null}
                </Box>
              )}

              {/* Balance Chart */}
              {selectedGraph === 'balance' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#ff9800', fontWeight: 600 }}>
                  {selectedCoin} Balance
                </Typography>
                {coinPriceBalanceData && coinPriceBalanceData.data ? (
                  <ApexChart
                    type="area"
                    height={120}
                    series={[
                      {
                        name: 'Balance',
                        data: coinPriceBalanceData.data.map(point => [point.timestamp, point.balance])
                      }
                    ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        background: '#fff',
                        animations: { enabled: true },
                        sparkline: false,
                        fontFamily: 'inherit',
                      },
                      dataLabels: { enabled: false },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tooltip: { enabled: false },
                        crosshairs: { show: false },
                      },
                      yaxis: {
                        show: true,
                        labels: {
                          show: true,
                          style: { fontSize: '10px', color: '#ff9800' },
                          formatter: val => (val ? val.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '0.00000000')
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },

                        // Ensure small values are visible by setting minimum scale
                        min: 0,
                        forceNiceScale: true,
                        floating: false
                      },
                                            stroke: { 
                        width: 3, 
                        curve: 'straight',
                        colors: ['#ff9800']
                      },
                      colors: ['#ff9800'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.3,
                          gradientToColors: ['#ff9800'],
                          inverseColors: false,
                          opacityFrom: 0.6,
                          opacityTo: 0.1,
                          stops: [0, 100]
                        }
                      },
                      markers: { size: 0 },
              tooltip: {
                enabled: true,
                x: { format: 'dd MMM yyyy HH:mm' },
                        y: {
                          formatter: val => (val ? val.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '0.00000000')
                },
                theme: 'light',
                        style: { fontSize: '12px' },
                marker: { show: false },
              },
              grid: {
                show: true,
                borderColor: '#e0e0e0',
                        strokeDashArray: 4,
                        xaxis: { lines: { show: true } },
                yaxis: { lines: { show: true } },
                      },
                      legend: { show: false },
                    }}
                  />
                ) : null}
              </Box>
              )}

              {/* Market Value Chart */}
              {selectedGraph === 'marketValue' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#4caf50', fontWeight: 600 }}>
                  {selectedCoin} Market Value
                </Typography>
                {coinPriceBalanceData && coinPriceBalanceData.data ? (
                  <ApexChart
                    type="area"
                    height={120}
                    series={[
                      {
                        name: 'Market Value',
                        data: coinPriceBalanceData.data.map(point => [point.timestamp, point.marketValue])
                      }
                    ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        background: '#fff',
                        animations: { enabled: true },
                        sparkline: false,
                        fontFamily: 'inherit',
                      },
                      dataLabels: { enabled: false },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tooltip: { enabled: false },
                        crosshairs: { show: false },
                      },
                      yaxis: {
                        show: true,
                        labels: {
                          show: true,
                          style: { fontSize: '10px', color: '#4caf50' },
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },

                      },
                      stroke: { 
                        width: 3, 
                        curve: 'straight',
                        colors: ['#4caf50']
                      },
                      colors: ['#4caf50'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.3,
                          gradientToColors: ['#4caf50'],
                          inverseColors: false,
                          opacityFrom: 0.6,
                          opacityTo: 0.1,
                          stops: [0, 100]
                        }
                      },
                      markers: { size: 0 },
                      tooltip: {
                        enabled: true,
                        x: { format: 'dd MMM yyyy HH:mm' },
                        y: {
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        theme: 'light',
                        style: { fontSize: '12px' },
                        marker: { show: false },
                      },
                      grid: {
                        show: true,
                        borderColor: '#e0e0e0',
                        strokeDashArray: 4,
                        xaxis: { lines: { show: true } },
                        yaxis: { lines: { show: true } },
                      },
                      legend: { show: false },
                    }}
                  />
                ) : null}
              </Box>
              )}

              {/* Cost Chart */}
              {selectedGraph === 'cost' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#e91e63', fontWeight: 600 }}>
                  {selectedCoin} Cost Over Time
                </Typography>
                {coinPriceBalanceData && coinPriceBalanceData.data ? (
                  <ApexChart
                    type="area"
                    height={120}
                    series={[
                      {
                        name: 'Cost',
                        data: coinPriceBalanceData.data.map(point => [point.timestamp, point.cost])
                      }
                    ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        background: '#fff',
                        animations: { enabled: true },
                        sparkline: false,
                        fontFamily: 'inherit',
                      },
                      dataLabels: { enabled: false },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tooltip: { enabled: false },
                        crosshairs: { show: false },
                      },
                      yaxis: {
                        show: true,
                        labels: {
                          show: true,
                          style: { fontSize: '10px', color: '#e91e63' },
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },

                      },
                      stroke: { 
                        width: 3, 
                        curve: 'straight',
                        colors: ['#e91e63']
                      },
                      colors: ['#e91e63'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.3,
                          gradientToColors: ['#e91e63'],
                          inverseColors: false,
                          opacityFrom: 0.6,
                          opacityTo: 0.1,
                          stops: [0, 100]
                        }
                      },
                      markers: { size: 0 },
                      tooltip: {
                        enabled: true,
                        x: { format: 'dd MMM yyyy HH:mm' },
                        y: {
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        theme: 'light',
                        style: { fontSize: '12px' },
                        marker: { show: false },
                      },
                      grid: {
                        show: true,
                        borderColor: '#e0e0e0',
                        strokeDashArray: 4,
                        xaxis: { lines: { show: true } },
                        yaxis: { lines: { show: true } },
                      },
                      legend: { show: false },
                    }}
                  />
                ) : null}
              </Box>
              )}

              {/* Combined Market Value & Cost Chart */}
              {selectedGraph === 'marketValueCost' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#9c27b0', fontWeight: 600 }}>
                  {selectedCoin} Market Value & Cost
                </Typography>
                {coinPriceBalanceData && coinPriceBalanceData.data ? (
                  <ApexChart
                    type="line"
                    height={120}
                                      series={[
                    {
                      name: 'Market Value',
                      type: 'line',
                      data: coinPriceBalanceData.data.map(point => [point.timestamp, point.marketValue])
                    },
                    {
                      name: 'Cost',
                      type: 'line',
                      data: coinPriceBalanceData.data.map(point => [point.timestamp, point.cost])
                    }
                  ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        background: '#fff',
                        animations: { enabled: true },
                        sparkline: false,
                        fontFamily: 'inherit',
                      },
                      dataLabels: { enabled: false },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tooltip: { enabled: false },
                        crosshairs: { show: false },
                      },
                      yaxis: {
                        // Single Y-axis for both Market Value and Cost to ensure correct visual relationship
                        show: true,
                        labels: {
                          show: true,
                          style: { fontSize: '10px', color: '#666' },
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'))
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },

                        min: (() => {
                          const marketValues = coinPriceBalanceData.data.filter(point => point.marketValue !== null && point.marketValue !== undefined && !isNaN(point.marketValue)).map(point => point.marketValue);
                          const costValues = coinPriceBalanceData.data.filter(point => point.cost !== null && point.cost !== undefined && !isNaN(point.cost)).map(point => point.cost);
                          const allValues = [...marketValues, ...costValues];
                          return allValues.length > 0 ? Math.min(...allValues) * 0.95 : undefined;
                        })(),
                        max: (() => {
                          const marketValues = coinPriceBalanceData.data.filter(point => point.marketValue !== null && point.marketValue !== undefined && !isNaN(point.marketValue)).map(point => point.marketValue);
                          const costValues = coinPriceBalanceData.data.filter(point => point.cost !== null && point.cost !== undefined && !isNaN(point.cost)).map(point => point.cost);
                          const allValues = [...marketValues, ...costValues];
                          return allValues.length > 0 ? Math.max(...allValues) * 1.05 : undefined;
                        })(),
                        forceNiceScale: false,
                        floating: false
                      },
                      stroke: { 
                        width: [2, 2], 
                        curve: 'straight'
                      },
                      colors: ['#4caf50', '#e91e63'],
                      markers: { size: 0 },
                      tooltip: {
                        enabled: true,
                        x: { format: 'dd MMM yyyy HH:mm' },
                        y: {
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        theme: 'light',
                        style: { fontSize: '12px' },
                        marker: { show: false },
                      },
              grid: {
                show: true,
                borderColor: '#e0e0e0',
                        strokeDashArray: 4,
                        xaxis: { lines: { show: true } },
                yaxis: { lines: { show: true } },
              },
              legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'left',
                        fontSize: '10px',
                        markers: { width: 8, height: 8 },
                itemMargin: { horizontal: 10 }
                      },
                    }}
                  />
                ) : null}
              </Box>
              )}

              {/* Gains/Losses Chart */}
              {selectedGraph === 'gains' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#ff5722', fontWeight: 600 }}>
                  {selectedCoin} Gains/Losses
                </Typography>
                {coinPriceBalanceData && coinPriceBalanceData.data ? (
                  <ApexChart
                    type="area"
                    height={120}
                    series={[
                      {
                        name: 'Gains/Losses',
                        data: coinPriceBalanceData.data.map(point => [point.timestamp, point.marketValue - point.cost])
                      }
                    ]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        background: '#fff',
                        animations: { enabled: true },
                        sparkline: false,
                        fontFamily: 'inherit',
                      },
                      dataLabels: { enabled: false },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', color: '#888' }, show: true, rotate: 0, datetimeUTC: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tooltip: { enabled: false },
                        crosshairs: { show: false },
                      },
                      yaxis: {
                        show: true,
                        labels: {
                          show: true,
                          style: { fontSize: '10px', color: '#ff5722' },
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },

                      },
                      stroke: { 
                        width: 3, 
                        curve: 'straight',
                        colors: ['#ff5722']
                      },
                      colors: ['#ff5722'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.3,
                          gradientToColors: ['#ff5722'],
                          inverseColors: false,
                          opacityFrom: 0.6,
                          opacityTo: 0.1,
                          stops: [0, 100]
                        }
                      },
                      markers: { size: 0 },
                      tooltip: {
                        enabled: true,
                        x: { format: 'dd MMM yyyy HH:mm' },
                        y: {
                          formatter: val => ('$' + (val ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'))
                        },
                        theme: 'light',
                        style: { fontSize: '12px' },
                        marker: { show: false },
                      },
                      grid: {
                        show: true,
                        borderColor: '#e0e0e0',
                        strokeDashArray: 4,
                        xaxis: { lines: { show: true } },
                        yaxis: { lines: { show: true } },
                      },
                      legend: { show: false },
                    }}
                  />
                ) : null}
          </Box>
              )}
            </Box>


          ) : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 0.2, flexWrap: 'nowrap', pb: 0.5 }}>
          {PORTFOLIO_RANGES.map(r => (
            <Button
              key={r.value}
              onClick={() => setChartRange(r.value)}
              variant="text"
              sx={{
                borderRadius: 1,
                minWidth: 32,
                fontWeight: 400,
                fontSize: '0.85rem',
                px: 0.8,
                py: 0.2,
                textTransform: 'none',
                bgcolor: chartRange === r.value ? '#e6f0fa' : 'transparent',
                color: chartRange === r.value ? '#1976d2' : '#222',
                boxShadow: 'none',
                border: 'none',
                transition: 'background 0.15s',
                height: 28,
                lineHeight: 1.1,
                '&:hover': {
                  bgcolor: chartRange === r.value ? '#d0e6fa' : '#f5f5f5',
                  color: chartRange === r.value ? '#1976d2' : '#222',
                },
              }}
            >
              {r.label}
            </Button>
          ))}
        </Box>
      </Box>
      )}

      {/* Sync Progress */}
      <SyncProgress 
        syncProgress={syncProgress} 
        onClose={clearSyncProgress} 
      />

      {/* Error Display */}
      {(error || syncError) && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => {
            setError('');
            clearError();
          }}
        >
          {error || syncError}
        </Alert>
      )}





      {/* Portfolio Overview Content */}
      <GroupedBalances
        balances={groupedBalances}
        loading={syncLoading}
        error={syncError}
        onRefresh={refreshGroupedBalances}
        currentPrices={prices}
        formatCurrency={formatCurrency}
        portfolioGains={portfolioGains}
        onAddHoldings={() => setOpen(true)}
        showNumbers={showNumbers}
      />

      <Dialog open={open} onClose={() => handleClose(true)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTransaction ? t('portfolio.editHoldings') : t('portfolio.addHoldings')}</DialogTitle>
        <Tabs
          value={modalType}
          onChange={(_, newValue) => setModalType(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('portfolio.addWallet')} value="wallet" />
          <Tab label={editingTransaction ? t('portfolio.editManualTransaction') : t('portfolio.addManualTransaction')} value="transaction" />
        </Tabs>
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {modalType === 'wallet' ? (
            <>
              {modalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {modalError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth required error={Boolean(!!modalError && !newWallet.blockchain)}>
                  <InputLabel>{t('common.blockchain')}</InputLabel>
                  <Select
                    value={newWallet.blockchain}
                    label={t('common.blockchain')}
                    onChange={(e) => setNewWallet(prev => ({ ...prev, blockchain: e.target.value }))}
                    disabled={blockchainsLoading}
                  >
                    {blockchains.map((chain, index) => (
                      <MenuItem 
                        key={chain.symbol || `blockchain-${index}`} 
                        value={(chain.symbol || '').toLowerCase()}
                        disabled={!chain.is_supported}
                        sx={{
                          opacity: chain.is_supported ? 1 : 0.5,
                          color: chain.is_supported ? 'inherit' : 'text.disabled'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={chain.icon_url} 
                            alt={chain.name} 
                            style={{ 
                              width: 24, 
                              height: 24,
                              opacity: chain.is_supported ? 1 : 0.5
                            }} 
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{chain.name}</span>
                            {!chain.is_supported && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}>
                                Coming Soon
                              </span>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('common.address')}
                  value={newWallet.address}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(!!modalError && !newWallet.address)}
                  helperText={modalError && !newWallet.address ? t('common.enterAddress') : ''}
                />
                <TextField
                  label={t('common.walletLabel')}
                  value={newWallet.label}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, label: e.target.value }))}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={() => handleClose(true)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" sx={{ minWidth: 100 }}>
                  {t('common.addWallet')}
                </Button>
              </Box>
            </>
          ) : (
            <AddTransactionModal
              formOnly={true}
              onClose={() => handleClose(true)}
              onSubmit={handleAddManualHolding}
              loading={coinsLoading}
              initialValues={newTransaction}
                                error={!!modalError}
              submitButtonText={editingTransaction ? t('portfolio.updateTransaction') : t('portfolio.addTransaction')}
              editing={!!editingTransaction}
            />
          )}
        </Box>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleColumnMenuClose}
      >
        {allColumns.map((col) => (
          <MenuItem
            key={col.id}
            disabled={visibleColumns.length === 1 && visibleColumns.includes(col.id)}
          >
            <Checkbox
              checked={visibleColumns.includes(col.id)}
              onChange={() => handleToggleColumn(col.id)}
            />
            {t(`tables.portfolio.${col.id}`)}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={columnsDialogOpen} onClose={handleCloseColumnsDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.columns')}</DialogTitle>
        <DialogContent>
          <List>
            {tempVisibleColumns.map((colId, index) => {
              const col = allColumns.find(c => c.id === colId);
              return (
                <ListItem
                  key={colId + '-' + index}
                  style={{ display: 'flex', alignItems: 'center' }}
                  secondaryAction={
                    <>
                      <IconButton edge="end" onClick={() => handleMoveColumnUp(index)} disabled={index === 0}>
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleMoveColumnDown(index)} disabled={index === tempVisibleColumns.length - 1}>
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleHideColumn(colId)} disabled={tempVisibleColumns.length === 1}>
                        <VisibilityOffIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={t(`tables.portfolio.${colId}`)} />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            {tempHiddenColumns.map((colId, index) => {
              const col = allColumns.find(c => c.id === colId);
              return (
                <ListItem key={colId + '-' + index} secondaryAction={
                  <IconButton edge="end" onClick={() => handleShowColumn(colId)}>
                    <VisibilityIcon />
                  </IconButton>
                }>
                  <ListItemText primary={t(`tables.portfolio.${colId}`)} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseColumnsDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveColumnsDialog} variant="contained" autoFocus>{t('common.saveChanges')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('dialogs.deleteWallet.title')}
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {t('dialogs.deleteWallet.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            {t('dialogs.deleteWallet.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// Add CSS animation for pulse effect
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
  }
`;
document.head.appendChild(style);

export default PortfolioPage; 